import { useEffect, useRef, useState } from 'react';
import { CardRecord, CardType, LocalParagraphScore, ParagraphRecallRecord, WritingContent, saveStudyLog, SrsFeedback, CardMode } from '../../db/indexedDb';
import { seedInitialData } from '../../db/seedData';
import { buildMixedQueue, getTodayPlan } from '../../srs/dashboard';
import { SessionController, type SessionSnapshot, loadPersistedSession, clearPersistedSession } from '../../srs/sessionController';
import type { PersistedReview } from '../../srs/reviewDb';
import { buildVocabQueue, type VocabPracticeCard } from '../../services/srsQueue/vocabQueue';
import { WritingClozeResult } from '../../writing/lightReview/WritingClozeCard';
import { ListeningResult } from '../../listening/types';
import { TopicRecallResult } from '../../writing/topicRecall/TopicRecallCard';
import { TopicDictationResult } from '../../writing/topicDictation';
import type { WritingModeType } from '../../config/writingMode';

export type LoadState =
  | { status: 'loading'; cards: CardRecord[]; type?: CardType }
  | { status: 'error'; cards: CardRecord[]; message: string; type?: CardType }
  | { status: 'ready'; cards: CardRecord[]; type?: CardType };

export interface SessionState {
  loadState: LoadState;
  snapshot: SessionSnapshot | undefined;
  saving: boolean;
  error: string | undefined;
  lastOutcome: PersistedReview | undefined;
  cardError: string | null;
  renderTick: number;
  ended: boolean;
  // 卡片结果状态
  lastWritingScore: LocalParagraphScore | null;
  lastRecallRecord: ParagraphRecallRecord | null;
  lastWritingCloze: WritingClozeResult | null;
  lastListeningResult: ListeningResult | null;
  topicRecallResult: TopicRecallResult | null;
  topicDictationResult: TopicDictationResult | null;
  // 交互状态
  hasNewAction: boolean;
  hasSpeakingRecording: boolean;
  hasWritingAnswer: boolean;
  aiWritingEvaluated: boolean;
  // 时间追踪
  cardStartTime: number | null;
}

export interface SessionActions {
  setSaving: (saving: boolean) => void;
  setError: (error: string | undefined) => void;
  setLastOutcome: (outcome: PersistedReview | undefined) => void;
  setCardError: (error: string | null) => void;
  setSnapshot: (snapshot: SessionSnapshot | undefined) => void;
  setRenderTick: React.Dispatch<React.SetStateAction<number>>;
  setEnded: (ended: boolean) => void;
  // 卡片结果 setter
  setLastWritingScore: (score: LocalParagraphScore | null) => void;
  setLastRecallRecord: (record: ParagraphRecallRecord | null) => void;
  setLastWritingCloze: (result: WritingClozeResult | null) => void;
  setLastListeningResult: (result: ListeningResult | null) => void;
  setTopicRecallResult: (result: TopicRecallResult | null) => void;
  setTopicDictationResult: (result: TopicDictationResult | null) => void;
  // 交互状态 setter
  setHasNewAction: (hasAction: boolean) => void;
  setHasSpeakingRecording: (hasRecording: boolean) => void;
  setHasWritingAnswer: (hasAnswer: boolean) => void;
  setAiWritingEvaluated: (evaluated: boolean) => void;
  // 时间追踪
  logStudyTime: (cardId: string | undefined, cardType: CardType, mode: CardMode, feedback: SrsFeedback, score?: number) => Promise<void>;
}

export function useSessionState(
  typeFilter: CardType | undefined,
  isMixed: boolean,
  modeFilter?: CardMode,
  options?: { writingModeOverride?: WritingModeType }
) {
  const controllerRef = useRef<SessionController>();
  const writingModeOverride = options?.writingModeOverride;
  const [loadState, setLoadState] = useState<LoadState>({ status: 'loading', cards: [], type: typeFilter });
  const [snapshot, setSnapshot] = useState<SessionSnapshot>();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>();
  const [lastOutcome, setLastOutcome] = useState<PersistedReview>();
  const [cardError, setCardError] = useState<string | null>(null);
  const [renderTick, setRenderTick] = useState(0);
  const [ended, setEnded] = useState(false);

  // 卡片结果状态
  const [lastWritingScore, setLastWritingScore] = useState<LocalParagraphScore | null>(null);
  const [lastRecallRecord, setLastRecallRecord] = useState<ParagraphRecallRecord | null>(null);
  const [lastWritingCloze, setLastWritingCloze] = useState<WritingClozeResult | null>(null);
  const [lastListeningResult, setLastListeningResult] = useState<ListeningResult | null>(null);
  const [topicRecallResult, setTopicRecallResult] = useState<TopicRecallResult | null>(null);
  const [topicDictationResult, setTopicDictationResult] = useState<TopicDictationResult | null>(null);

  // 交互状态
  const [hasNewAction, setHasNewAction] = useState(false);
  const [hasSpeakingRecording, setHasSpeakingRecording] = useState(false);
  const [hasWritingAnswer, setHasWritingAnswer] = useState(false);
  const [aiWritingEvaluated, setAiWritingEvaluated] = useState(false);

  // 时间追踪
  const [cardStartTime, setCardStartTime] = useState<number | null>(null);

  // 加载队列
  useEffect(() => {
    let active = true;
    setLoadState({ status: 'loading', cards: [], type: typeFilter });
    setError(undefined);
    setLastOutcome(undefined);

    (async () => {
      try {
        await seedInitialData();
        const { plan } = await getTodayPlan(undefined, undefined, writingModeOverride ? { writingModeOverride } : undefined);
        const skillsWithVocab = plan.skills.map((skill) =>
          skill.type === 'vocab'
            ? { ...skill, plannedCards: buildVocabQueue(skill.plannedCards) }
            : skill
        );
        const adjustedPlan = { ...plan, skills: skillsWithVocab };

        let queue = typeFilter
          ? adjustedPlan.skills.find((skill) => skill.type === typeFilter)?.plannedCards ?? []
          : isMixed
            ? buildMixedQueue(adjustedPlan)
            : adjustedPlan.skills.flatMap((skill) => skill.plannedCards);

        // 如果指定了 modeFilter，进一步过滤队列
        if (modeFilter) {
          queue = queue.filter((card) => card.mode === modeFilter);
        }

        if (!active) return;
        setLoadState({ status: 'ready', cards: queue, type: typeFilter });
      } catch (err) {
        if (!active) return;
        const message = err instanceof Error ? err.message : '加载练习队列失败';
        setLoadState({ status: 'error', cards: [], message, type: typeFilter });
      }
    })();

    return () => {
      active = false;
    };
  }, [isMixed, typeFilter, modeFilter, writingModeOverride]);

  // 初始化 controller
  useEffect(() => {
    if (loadState.status !== 'ready') return;

    // 尝试恢复之前的 session 进度
    const persisted = loadPersistedSession();
    const controller = new SessionController(loadState.cards, undefined, {
      typeFilter: typeFilter,
      isMixed: isMixed,
    });

    // 检查是否可以恢复（同类型 session 且队列匹配）
    if (persisted) {
      const sameType = (persisted.typeFilter ?? '') === (typeFilter ?? '') &&
                       (persisted.isMixed ?? false) === isMixed;
      if (sameType && controller.restoreFrom(persisted)) {
        // 恢复成功
        console.log('[Session] 恢复进度:', persisted.index, '/', persisted.cardIds.length);
      } else {
        // 类型不匹配或队列变化，清除旧状态
        clearPersistedSession();
      }
    }

    controllerRef.current = controller;
    setSnapshot(controller.snapshot());

    // 初始持久化
    controller.persist();
  }, [loadState, typeFilter, isMixed]);

  // 切换卡片时重置状态
  const activeCard = snapshot?.currentCard;
  const activePracticeKey = activeCard
    ? (activeCard as VocabPracticeCard).practiceId ?? activeCard.id
    : undefined;

  useEffect(() => {
    setCardError(null);
    setRenderTick((tick) => tick + 1);
    setLastWritingScore(null);
    setLastRecallRecord(null);
    setLastWritingCloze(null);
    setLastListeningResult(null);
    setHasNewAction(false);
    setHasSpeakingRecording(false);
    setHasWritingAnswer(false);
    setTopicRecallResult(null);
    setTopicDictationResult(null);
    setAiWritingEvaluated(false);
    // 记录新卡片的开始时间
    setCardStartTime(Date.now());
  }, [activePracticeKey]);

  // 记录学习时间
  const logStudyTime = async (
    cardId: string | undefined,
    cardType: CardType,
    mode: CardMode,
    feedback: SrsFeedback,
    score?: number
  ) => {
    const now = Date.now();
    const startTime = cardStartTime ?? now;
    const durationMs = now - startTime;

    // 仅记录有效的学习时间（至少 3 秒，最多 30 分钟）
    if (durationMs < 3000 || durationMs > 30 * 60 * 1000) {
      return;
    }

    try {
      await saveStudyLog({
        cardId,
        cardType,
        mode,
        startedAt: startTime,
        endedAt: now,
        durationMs,
        feedback,
        score,
      });
    } catch (err) {
      console.warn('[StudyLog] 保存学习记录失败:', err);
    }
  };

  const state: SessionState = {
    loadState,
    snapshot,
    saving,
    error,
    lastOutcome,
    cardError,
    renderTick,
    ended,
    lastWritingScore,
    lastRecallRecord,
    lastWritingCloze,
    lastListeningResult,
    topicRecallResult,
    topicDictationResult,
    hasNewAction,
    hasSpeakingRecording,
    hasWritingAnswer,
    aiWritingEvaluated,
    cardStartTime,
  };

  const actions: SessionActions = {
    setSaving,
    setError,
    setLastOutcome,
    setCardError,
    setSnapshot,
    setRenderTick,
    setEnded,
    setLastWritingScore,
    setLastRecallRecord,
    setLastWritingCloze,
    setLastListeningResult,
    setTopicRecallResult,
    setTopicDictationResult,
    setHasNewAction,
    setHasSpeakingRecording,
    setHasWritingAnswer,
    setAiWritingEvaluated,
    logStudyTime,
  };

  return { state, actions, controllerRef };
}
